import graphql from 'graphql.js';
import {
    IGeoDistanceStoreDetailsRequest,
    IStoreByIdResult,
    IStoresByIdsRequest,
    IStoresByIdsResult,
    IStoresNearPointRequest,
    IStoresNearPointResult,
    IStoresDirectoryResult,
    IActiveFilters
} from './Interfaces';
import { events, queryBuilder, queryfy, storeServices } from './QueryBuilder';
import { getClientISODate } from '../utils/DateUtils';
import { ErrorHandler } from './ErrorHandler';
import { query, serviceQueries } from '@estee/elc-service-bus';

export interface IApiConfig {
    baseUrl: string;
    businessUnit: string;
    showOabServices?: boolean;
    showEvents?: boolean;
    areFiltersEnabled?: boolean;
}

interface IStoresApiConfig extends IApiConfig {
    clientId: string;
}

export const UNAUTHORIZED_MESSAGE_401 = 'Unauthorized';
export const UNAUTHORIZED_MESSAGE_403 =
    'User is not authorized to access this resource with an explicit deny';

export class StoresApiSdk {
    private readonly storesUrl: string;
    private readonly businessUnit: string;
    private readonly baseUrl: string;
    private readonly showOabServices?: boolean;
    private readonly showEvents?: boolean;
    private readonly areFiltersEnabled?: boolean;
    private readonly errorHandler: ErrorHandler;
    private readonly clientId: string;

    constructor(config: IStoresApiConfig) {
        this.baseUrl = config.baseUrl;
        this.businessUnit = config.businessUnit;
        this.storesUrl = `${this.baseUrl}/stardust-stores-store-api-v2/graphql`;
        this.errorHandler = new ErrorHandler();
        this.showOabServices = config.showOabServices;
        this.showEvents = config.showEvents;
        this.areFiltersEnabled = config.areFiltersEnabled;
        this.clientId = config.clientId;
    }

    public async headers() {
        const authToken = await query(serviceQueries.GET_GUEST_AUTH_TOKEN);

        return {
            'business-unit': this.businessUnit,
            'Cache-Control': 'no-cache',
            // this is a temporary requirement
            ClientId: this.clientId,
            // @ts-ignore
            authorizationtoken: authToken?.token || authToken
        };
    }

    public get client() {
        return async (schema: string) =>
            graphql(this.storesUrl, {
                headers: await this.headers(),
                fragments: {
                    getStoresByGeo: queryBuilder.getStoresByGeo(this.showEvents),
                    getStoreById: queryBuilder.getStoreById,
                    getStoresByIds: queryBuilder.getStoresByIds,
                    getStoresDirectory: queryBuilder.getStoresDirectory
                },
                asJSON: true
            })(schema);
    }

    private get getStoreByIdRequestDetails() {
        let defaultRequest = '...getStoreById';

        if (this.showOabServices) {
            defaultRequest = `${defaultRequest}, ... on Store {${storeServices}}`;
        }

        if (this.showEvents) {
            defaultRequest = `
                ${defaultRequest}
                ${events}
            `;
        }

        return defaultRequest;
    }

    public getStoresNearPoint = async (
        request: IStoresNearPointRequest,
        filters: IActiveFilters = {},
        isOAB?: boolean
    ): Promise<IStoresNearPointResult | void> => {
        const searchParams = {
            geographical: request,
            clientDetails: {
                clientISOTime: getClientISODate()
            },
            ...(!isOAB && this.areFiltersEnabled && { filters })
        };

        return this.errorHandler.tryRequest(
            await this.client(`{
                storesNearPoint(
                    searchParams: ${queryfy(searchParams)}
                )
                {
                    items {
                        services {
                            name
                            price
                            totalDuration
                            totalDurationWithPadding
                            id
                            onlineContentId
                            virtual
                            category {
                                id
                                name
                                parentId
                            }
                        }
                    }
                    ...getStoresByGeo
                }
            }`)
        );
    };

    public getStoreBydId = async (
        request: IGeoDistanceStoreDetailsRequest
    ): Promise<IStoreByIdResult | void> => {
        const searchParams = {
            ...request,
            clientDetails: {
                clientISOTime: getClientISODate()
            }
        };

        return this.errorHandler.tryRequest(
            await this.client(`{
                store(
                    searchParams: ${queryfy(searchParams)}
                ) {
                    ${this.getStoreByIdRequestDetails}
                }
             }`)
        );
    };

    public getStoreBydIds = async (
        request: IStoresByIdsRequest
    ): Promise<IStoresByIdsResult | void> =>
        this.errorHandler.tryRequest(
            await this.client(`{
                storesByMultipleIds( searchParams: ${queryfy(request)} ) {
                    ...getStoresByIds
                }
            }`)
        );

    public getStoresDirectory = async (): Promise<IStoresDirectoryResult | undefined> =>
        this.errorHandler.tryRequest(
            await this.client(`{
                storesDirectory {
                    ...getStoresDirectory
                }
            }`)
        );
}
